import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AlertService, ItemToSellPicture } from 'src/app/_services';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Angulartics2 } from 'angulartics2';
import { PictureService } from 'src/app/_services/picture.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'image-list-editor',
  templateUrl: './image-list-editor.component.html',
  styleUrls: ['./image-list-editor.component.scss']
})
export class ImageListEditorComponent implements OnInit {
  
  private static PX_PER_IMAGE = 160;
  
  imageSrc: string;
  uploading: boolean = false;
  numberOfPicturesUploading = 0;
  picturesUrl = environment.picturesUrl;
  lists: ItemToSellPicture[][] = [];
  resizeObserver: ResizeObserver;
  picturesPerRow: number;
  pictureVerificationDisabled: boolean = false;
  picturesWithWarning: ItemToSellPicture[] = [];
  
  @Input() maxImages: number = 30;
  @Input() pictures: ItemToSellPicture[] = [];
  @Output() onChange: EventEmitter<void> = new EventEmitter();
  @Output() onUploadingStateChange: EventEmitter<boolean> = new EventEmitter();
  
  @ViewChild("pictureEditor") pictureEditor: ElementRef;
  
  constructor(
    private s3Service: PictureService, 
    private alertService: AlertService,
    private angulartics: Angulartics2) {
  }
  
  public ngOnInit(): void {
    this.sortByProperty();
    this.calculateLists();
  }
  
  public ngAfterViewInit() {
    
    this.resizeObserver = new ResizeObserver(entries => {
      this.recalculateNumberOfPicturesPerRow(entries[0].contentRect.width);
      this.calculateLists();
    });
    
    this.resizeObserver.observe(this.pictureEditor.nativeElement);
  }
  
  private recalculateNumberOfPicturesPerRow(width: number) {
    this.picturesPerRow = Math.floor(width / ImageListEditorComponent.PX_PER_IMAGE);
    console.log("Pictures per row " + this.picturesPerRow);
  }
  
  public selectFile(filesList: FileList | null, replace: boolean = false) {
    
    if (!filesList) {
      return;
    }
    
    this.uploading = true;
    this.onUploadingStateChange.emit(true);
    
    Array.from(filesList).forEach(fileToUpload => {
    
      console.log(fileToUpload.name);
      
      var reader = new FileReader();        
      reader.readAsDataURL(fileToUpload);
      reader.onload = () => {
        
        this.numberOfPicturesUploading++;
        this.imageSrc = reader.result as string;
        replace ? this.replaceImage(fileToUpload) : this.addImage(fileToUpload);
      };
    })
  }
  
  async replaceImage(file: File) {
    
    try {
      const uploadedPicture = await this.s3Service.upload(file);
      this.pictures[0] = new ItemToSellPicture(uploadedPicture.key, this.pictures.length);
      this.calculateLists();
      this.onChange.emit();
      
      this.angulartics.eventTrack.next({
        action: "Replacement picture uploaded",
        properties: { 
          category: 'Picture list editor',
          pictureKey: uploadedPicture.key 
        }
      })
    } finally {
      this.numberOfPicturesUploading--;
      
      if (this.numberOfPicturesUploading === 0) {
        this.uploading = false;
        this.onUploadingStateChange.emit(false);
      }
    }
  }

  async addImage(file: File) {
    
    try {
      if (!(await this.isValidImage(file))) {
        console.log("Selected image not valid");
        this.alertService.showError($localize `This photo is too small. Please consider taking an additional picture to increase the chances to selling.`);
      }
      
      const uploadedPicture = await this.s3Service.upload(file);
      
      if (this.pictures.length < this.maxImages) {
        this.pictures.push(new ItemToSellPicture(uploadedPicture.key, this.pictures.length));
        this.calculateLists();
        this.onChange.emit();

        this.angulartics.eventTrack.next({
          action: "Picture uploaded",
          properties: { 
            category: 'Picture list editor',
            pictureKey: uploadedPicture.key
          }
        })
      } else {
        console.log(`Skipping picture ${uploadedPicture.key}, too many pictures added`);        
      }

    } finally {
      this.numberOfPicturesUploading--;
      
      if (this.numberOfPicturesUploading === 0) {
        this.uploading = false;
        this.onUploadingStateChange.emit(false);
      }
    }
  }
  
  public delete(key: string) {
    this.pictures.splice(this.pictures.findIndex(picture => picture.key === key), 1);
    this.calculateLists();
    //TODO: delete file from S3
    this.onChange.emit();
  }

  checkLoadedPicture(event: any, picture: ItemToSellPicture) {
    
    if (event.srcElement.naturalWidth > 0 && event.srcElement.naturalWidth < 330) {
      console.log("Image too small " + event.srcElement.src);
      this.picturesWithWarning.push(picture);
      this.angulartics.eventTrack.next({
        action: "Picture warning",
        properties: { 
          category: 'Picture list editor',
          label: "Picture too small",
          pictureKey: picture.key
        }
      })
    }
  }
  
  hasWarning(picture: ItemToSellPicture): boolean {
    return this.picturesWithWarning.find(pictureWithWarning => pictureWithWarning.key === picture.key) !== undefined;
  }

  isValidImageElement(element: any): boolean {
    
    if (element.naturalWidth > 0 && element.naturalWidth < 330) {
      return false;
    }
    
    return true;
  }
  
  private async isValidImage(file: File): Promise<boolean> {
    const fr = new FileReader;
    const result = new Promise<boolean>((resolve, reject) => {
      fr.onload = function () { // file is loaded
        var img = new Image;
        img.onload = function () {
          if (img.width < 330) {
            console.log("Image width is too small");
            resolve(false);
          } else {
            resolve(true);
          }
        };
        img.src = fr.result as string; // is the data URL because called with readAsDataURL
      };
      fr.readAsDataURL(file); // I'm using a <input type="file"> for demonstrating
    });
    
    return result;
  }
  
  public drop(event: CdkDragDrop<string[]>) {
    
    const newIndex = +event.container.data[0] * this.picturesPerRow + event.currentIndex;
    const oldIndex = +event.previousContainer.data[0] * this.picturesPerRow + event.previousIndex;
    moveItemInArray(this.pictures, oldIndex, newIndex);
    console.log("New " + newIndex + ", old " + oldIndex);
      
    this.applyOrderToPictures();
    this.calculateLists();
    
    this.angulartics.eventTrack.next({
      action: "Pictures ordered",
      properties: { 
        category: 'Picture list editor',
        oldIndex: oldIndex,
        newIndex: newIndex
      },
    })
  }
  
  get shouldShowButtonInTheEnd(): boolean {
    const result = (this.pictures.length < this.maxImages) && (this.pictures.length === 0 || (this.pictures.length % this.picturesPerRow === 0));
    
    return result;
  }
  
  private calculateLists() {

    this.lists.length = 0;
    let currentRow = 1;    
    
    this.pictures.forEach((picture) => {
      if (this.lists.length < currentRow) {
        this.lists.push([]);
      }
      
      this.lists[currentRow - 1].push(picture);
      
      if (this.lists[currentRow - 1].length >= this.picturesPerRow) {
        currentRow++;
      }
    });
  }
  
  private sortByProperty() {
    this.pictures.sort((a, b) => a.order - b.order);
  }
  
  private applyOrderToPictures() {
    this.pictures.forEach((picture, index) => {
      picture.order = index;
    });
  }
  
  getSmallPictureTooltip(picture: ItemToSellPicture): string {
    return this.hasWarning(picture) ? $localize `Consider adding another photo to make the listing sell better` : ''
  }
}
